/**
 * A questionnaire figure drawn in the report.
 * Bars, the health-profile spider, the score scale and the trend line
 * all travel as this one shape. Word and the PDF preview embed a PNG.
 */

import { zlibSync } from 'fflate';
import { labelHeight, paintLabel, wrapLabel } from '@/lib/patientReports/radarFont';

export type ChartKind = 'bar' | 'radar' | 'scale' | 'history';

export type ChartPoint = {
  label: string;
  value: number;
  /** Right end of a scale band, when this point is a band. */
  max?: number;
};

export type ChartFigure = {
  /** Stable id for the on/off choice. Missing on older bars. */
  id?: string;
  label: string;
  /** Missing means a bar. Older reports only stored bars. */
  kind?: ChartKind;
  value: number;
  max: number;
  /** Spider axes, scale bands, or trend points. */
  points?: readonly ChartPoint[];
};

export function chartFillRatio(chart: Pick<ChartFigure, 'value' | 'max'>): number {
  if (!Number.isFinite(chart.value) || !Number.isFinite(chart.max) || chart.max <= 0) return 0;
  return Math.max(0, Math.min(1, chart.value / chart.max));
}

function formatChartNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function pointCaption(point: ChartPoint, fallbackMax: number): string {
  const label = point.label.trim();
  const value = formatChartNumber(point.value);
  const max = point.max ?? fallbackMax;
  if (!Number.isFinite(max) || max <= 0) return `${label}: ${value}`;
  return `${label}: ${value} / ${formatChartNumber(max)}`;
}

/** Words next to the figure. A bar is one line. Other figures add a short legend. */
export function chartCaption(chart: ChartFigure): string {
  const label = chart.label.trim();
  if (chart.kind === 'radar') return label;
  if (chart.kind === 'history') {
    const lines = [label];
    for (const point of chart.points ?? []) {
      if (!point.label.trim() || !Number.isFinite(point.value)) continue;
      lines.push(pointCaption(point, chart.max));
    }
    return lines.join('\n');
  }
  if (chart.kind === 'scale') {
    const value = formatChartNumber(chart.value);
    const head =
      Number.isFinite(chart.max) && chart.max > 0
        ? `${label}: ${value} / ${formatChartNumber(chart.max)}`
        : `${label}: ${value}`;
    const bands = (chart.points ?? [])
      .filter((point) => point.label.trim())
      .map((point) => {
        const end = point.max ?? point.value;
        return `${point.label.trim()} ${formatChartNumber(point.value)}–${formatChartNumber(end)}`;
      });
    return bands.length > 0 ? `${head}\n${bands.join(' · ')}` : head;
  }
  const value = formatChartNumber(chart.value);
  if (!Number.isFinite(chart.max) || chart.max <= 0) return `${label}: ${value}`;
  return `${label}: ${value} / ${formatChartNumber(chart.max)}`;
}

export function chartHasFigure(chart: ChartFigure): boolean {
  if (!chart.label.trim()) return false;
  if (chart.kind === 'radar' || chart.kind === 'history') return (chart.points?.length ?? 0) > 0;
  return Number.isFinite(chart.value);
}

const BAR_WIDTH = 640;
const BAR_HEIGHT = 18;
const RADAR_W = 1040;
const RADAR_H = 980;
const SCALE_WIDTH = 640;
const SCALE_HEIGHT = 22;
const HISTORY_WIDTH = 640;
const HISTORY_HEIGHT = 120;
const TRACK = [229, 228, 223] as const;
const FILL = [15, 118, 110] as const;
const FILL_SOFT = [210, 232, 229] as const;
const GRID = [214, 211, 204] as const;
const INK = [28, 25, 23] as const;
const WHITE = [255, 255, 255] as const;

const SCALE_COLORS = [
  [110, 196, 154],
  [190, 214, 110],
  [232, 196, 92],
  [232, 146, 74],
  [214, 96, 86],
] as const;

type RGB = readonly [number, number, number];

export function chartPixelSize(chart: Pick<ChartFigure, 'kind'>): { width: number; height: number } {
  if (chart.kind === 'radar') return { width: RADAR_W, height: RADAR_H };
  if (chart.kind === 'scale') return { width: SCALE_WIDTH, height: SCALE_HEIGHT };
  if (chart.kind === 'history') return { width: HISTORY_WIDTH, height: HISTORY_HEIGHT };
  return { width: BAR_WIDTH, height: BAR_HEIGHT };
}

/** PNG for Word and for Clinio's own PDF. Bars stay a flat track. */
export function chartPng(chart: ChartFigure): Uint8Array {
  if (chart.kind === 'radar') return chartRadarPng(chart);
  if (chart.kind === 'scale') return chartScalePng(chart);
  if (chart.kind === 'history') return chartHistoryPng(chart);
  return chartBarPng(chart);
}

/** Horizontal bar as a PNG. */
export function chartBarPng(chart: Pick<ChartFigure, 'value' | 'max'>): Uint8Array {
  const rgb = blank(BAR_WIDTH, BAR_HEIGHT, TRACK);
  const fillWidth = Math.round(BAR_WIDTH * chartFillRatio(chart));
  for (let y = 0; y < BAR_HEIGHT; y += 1) {
    for (let x = 0; x < fillWidth; x += 1) paint(rgb, BAR_WIDTH, x, y, FILL);
  }
  return encodePng(BAR_WIDTH, BAR_HEIGHT, rgb);
}

const LABEL = [120, 113, 108] as const;
const TICK = [168, 162, 158] as const;
const PROFILE_FILL = [207, 228, 226] as const;

/**
 * Same spider as the questionnaire Sundhedsprofil: first domain at the top,
 * then clockwise, scale 0–100, teal fill, domain names on the spokes.
 */
function chartRadarPng(chart: ChartFigure): Uint8Array {
  const points = (chart.points ?? []).filter((point) => point.label.trim() && Number.isFinite(point.value));
  const rgb = blank(RADAR_W, RADAR_H, WHITE);
  const cx = RADAR_W / 2;
  const cy = 430;
  const radius = 230;
  const count = Math.max(points.length, 3);
  const scale = 3;
  const at = (index: number, ratio: number): [number, number] => {
    const angle = 90 + (index * -360) / count;
    const rad = (-angle * Math.PI) / 180;
    return [cx + Math.cos(rad) * radius * ratio, cy + Math.sin(rad) * radius * ratio];
  };
  const ink = (color: RGB) => (x: number, y: number) => paint(rgb, RADAR_W, x, y, color);
  for (const ring of [0.25, 0.5, 0.75, 1]) {
    strokePolygon(
      rgb,
      RADAR_W,
      Array.from({ length: count }, (_, index) => at(index, ring)),
      GRID,
    );
  }
  for (let index = 0; index < count; index += 1) {
    const [x, y] = at(index, 1);
    line(rgb, RADAR_W, cx, cy, x, y, GRID);
  }
  for (const tick of [25, 50, 75, 100]) {
    const [x, y] = at(0, tick / 100);
    paintLabel(String(tick), 2, x + 8, y - labelHeight(2) / 2, 'left', ink(TICK));
  }
  if (points.length >= 3) {
    const poly = points.map((point, index) => at(index, Math.max(0, Math.min(1, point.value / 100))));
    fillPolygon(rgb, RADAR_W, poly, PROFILE_FILL);
    strokePolygon(rgb, RADAR_W, poly, FILL);
    strokePolygon(
      rgb,
      RADAR_W,
      poly.map(([x, y]) => [x + 1, y]),
      FILL,
    );
    for (const [x, y] of poly) disc(rgb, RADAR_W, x, y, 5, FILL);
  }
  points.forEach((point, index) => {
    const [x, y] = at(index, 1);
    const lines = wrapLabel(point.label.trim(), 240, scale);
    const align: 'left' | 'center' | 'right' = x > cx + 16 ? 'left' : x < cx - 16 ? 'right' : 'center';
    const lineH = labelHeight(scale) + 3;
    const block = lines.length * lineH;
    let top = y - block / 2;
    if (y < cy - radius * 0.55) top = y - radius * 0.16 - block;
    if (y > cy + radius * 0.55) top = y + radius * 0.12;
    const anchor = align === 'left' ? x + 14 : align === 'right' ? x - 14 : x;
    lines.forEach((row, lineIndex) => {
      paintLabel(row, scale, anchor, top + lineIndex * lineH, align, ink(LABEL));
    });
  });
  const danish = !/^health profile$/i.test(chart.label.trim());
  const scoreName = danish ? 'Samlet score' : 'Overall score';
  const scoreValue = `${(Math.round(chart.value * 10) / 10).toFixed(1)}/100`;
  const scoreY = cy + radius + 78;
  paintLabel(scoreName, 3, cx, scoreY, 'center', ink(TICK));
  paintLabel(scoreValue, 4, cx, scoreY + labelHeight(3) + 8, 'center', ink(INK));
  return encodePng(RADAR_W, RADAR_H, rgb);
}

function chartScalePng(chart: ChartFigure): Uint8Array {
  const rgb = blank(SCALE_WIDTH, SCALE_HEIGHT, TRACK);
  const bands = (chart.points ?? []).filter((point) => Number.isFinite(point.value));
  const end = chart.max > 0 ? chart.max : 1;
  if (bands.length === 0) {
    for (let y = 0; y < SCALE_HEIGHT; y += 1) {
      for (let x = 0; x < SCALE_WIDTH; x += 1) paint(rgb, SCALE_WIDTH, x, y, FILL_SOFT);
    }
  } else {
    let cursor = 0;
    bands.forEach((band, index) => {
      const from = Math.max(0, band.value);
      const to = Math.max(from, band.max ?? band.value);
      const x0 = Math.round((from / end) * SCALE_WIDTH);
      const x1 = Math.round((to / end) * SCALE_WIDTH);
      const color = SCALE_COLORS[index % SCALE_COLORS.length] ?? FILL;
      const left = Math.max(cursor, x0);
      const right = Math.max(left, x1);
      for (let y = 0; y < SCALE_HEIGHT; y += 1) {
        for (let x = left; x < right && x < SCALE_WIDTH; x += 1) paint(rgb, SCALE_WIDTH, x, y, color);
      }
      cursor = right;
    });
  }
  const ratio = chartFillRatio(chart);
  const marker = Math.round(ratio * (SCALE_WIDTH - 1));
  for (let y = 0; y < SCALE_HEIGHT; y += 1) {
    for (let dx = -1; dx <= 1; dx += 1) paint(rgb, SCALE_WIDTH, marker + dx, y, INK);
  }
  return encodePng(SCALE_WIDTH, SCALE_HEIGHT, rgb);
}

function chartHistoryPng(chart: ChartFigure): Uint8Array {
  const points = (chart.points ?? []).filter((point) => Number.isFinite(point.value));
  const rgb = blank(HISTORY_WIDTH, HISTORY_HEIGHT, WHITE);
  const left = 16;
  const right = HISTORY_WIDTH - 16;
  const top = 16;
  const bottom = HISTORY_HEIGHT - 20;
  const max = Math.max(chart.max, ...points.map((point) => point.value), 1);
  const yOf = (value: number) => bottom - (Math.max(0, value) / max) * (bottom - top);
  const xOf = (index: number) =>
    points.length <= 1
      ? (left + right) / 2
      : left + (index * (right - left)) / (points.length - 1);
  line(rgb, HISTORY_WIDTH, left, bottom, right, bottom, GRID);
  line(rgb, HISTORY_WIDTH, left, yOf(max / 2), right, yOf(max / 2), GRID);
  for (let index = 1; index < points.length; index += 1) {
    line(
      rgb,
      HISTORY_WIDTH,
      xOf(index - 1),
      yOf(points[index - 1]?.value ?? 0),
      xOf(index),
      yOf(points[index]?.value ?? 0),
      FILL,
    );
  }
  points.forEach((point, index) => {
    const last = index === points.length - 1;
    disc(rgb, HISTORY_WIDTH, xOf(index), yOf(point.value), last ? 5 : 4, last ? FILL : WHITE);
    disc(rgb, HISTORY_WIDTH, xOf(index), yOf(point.value), last ? 5 : 4, FILL, true);
  });
  return encodePng(HISTORY_WIDTH, HISTORY_HEIGHT, rgb);
}

/** Width and height inside the Word file, in EMUs (English Metric Units). */
export function chartEmu(chart: Pick<ChartFigure, 'kind'>): { cx: number; cy: number } {
  const emuPerPx = 9525;
  const size = chartPixelSize(chart);
  return { cx: size.width * emuPerPx, cy: size.height * emuPerPx };
}

/** @deprecated Use chartEmu. Kept so older callers still size a bar. */
export function chartBarEmu(): { cx: number; cy: number } {
  return chartEmu({ kind: 'bar' });
}

function blank(width: number, height: number, color: RGB): Uint8Array {
  const rgb = new Uint8Array(width * height * 3);
  for (let index = 0; index < rgb.length; index += 3) {
    rgb[index] = color[0];
    rgb[index + 1] = color[1];
    rgb[index + 2] = color[2];
  }
  return rgb;
}

function paint(rgb: Uint8Array, width: number, x: number, y: number, color: RGB): void {
  const px = Math.round(x);
  const py = Math.round(y);
  if (px < 0 || py < 0 || px >= width) return;
  const height = rgb.length / (width * 3);
  if (py >= height) return;
  const offset = (py * width + px) * 3;
  rgb[offset] = color[0];
  rgb[offset + 1] = color[1];
  rgb[offset + 2] = color[2];
}

function line(
  rgb: Uint8Array,
  width: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  color: RGB,
): void {
  let x = Math.round(x0);
  let y = Math.round(y0);
  const endX = Math.round(x1);
  const endY = Math.round(y1);
  const dx = Math.abs(endX - x);
  const dy = Math.abs(endY - y);
  const sx = x < endX ? 1 : -1;
  const sy = y < endY ? 1 : -1;
  let err = dx - dy;
  for (let guard = 0; guard < 4000; guard += 1) {
    paint(rgb, width, x, y, color);
    if (x === endX && y === endY) break;
    const doubled = 2 * err;
    if (doubled > -dy) {
      err -= dy;
      x += sx;
    }
    if (doubled < dx) {
      err += dx;
      y += sy;
    }
  }
}

function strokePolygon(rgb: Uint8Array, width: number, points: readonly [number, number][], color: RGB): void {
  for (let index = 0; index < points.length; index += 1) {
    const from = points[index];
    const to = points[(index + 1) % points.length];
    if (!from || !to) continue;
    line(rgb, width, from[0], from[1], to[0], to[1], color);
  }
}

function fillPolygon(rgb: Uint8Array, width: number, points: readonly [number, number][], color: RGB): void {
  if (points.length < 3) return;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minY = Math.min(minY, point[1]);
    maxY = Math.max(maxY, point[1]);
  }
  const height = rgb.length / (width * 3);
  const y0 = Math.max(0, Math.floor(minY));
  const y1 = Math.min(height - 1, Math.ceil(maxY));
  for (let y = y0; y <= y1; y += 1) {
    const xs: number[] = [];
    for (let index = 0; index < points.length; index += 1) {
      const from = points[index];
      const to = points[(index + 1) % points.length];
      if (!from || !to) continue;
      if ((from[1] <= y && to[1] > y) || (to[1] <= y && from[1] > y)) {
        const span = to[1] - from[1];
        if (span === 0) continue;
        xs.push(from[0] + ((y - from[1]) / span) * (to[0] - from[0]));
      }
    }
    xs.sort((left, right) => left - right);
    for (let index = 0; index + 1 < xs.length; index += 2) {
      const start = Math.max(0, Math.ceil(xs[index] ?? 0));
      const stop = Math.min(width - 1, Math.floor(xs[index + 1] ?? 0));
      for (let x = start; x <= stop; x += 1) paint(rgb, width, x, y, color);
    }
  }
}

function disc(
  rgb: Uint8Array,
  width: number,
  cx: number,
  cy: number,
  radius: number,
  color: RGB,
  ring = false,
): void {
  const r = Math.ceil(radius);
  for (let y = -r; y <= r; y += 1) {
    for (let x = -r; x <= r; x += 1) {
      const distance = Math.hypot(x, y);
      if (ring) {
        if (distance <= radius && distance >= radius - 1.6) paint(rgb, width, cx + x, cy + y, color);
      } else if (distance <= radius) {
        paint(rgb, width, cx + x, cy + y, color);
      }
    }
  }
}

function encodePng(width: number, height: number, rgb: Uint8Array): Uint8Array {
  const stride = width * 3;
  const raw = new Uint8Array(height * (1 + stride));
  for (let y = 0; y < height; y += 1) {
    const dest = y * (1 + stride);
    raw[dest] = 0;
    raw.set(rgb.subarray(y * stride, (y + 1) * stride), dest + 1);
  }
  const signature = [137, 80, 78, 71, 13, 10, 26, 10];
  const ihdr = new Uint8Array(13);
  writeUint32(ihdr, 0, width);
  writeUint32(ihdr, 4, height);
  ihdr[8] = 8;
  ihdr[9] = 2;
  const chunks = [chunk('IHDR', ihdr), chunk('IDAT', zlibSync(raw)), chunk('IEND', new Uint8Array(0))];
  const size = signature.length + chunks.reduce((sum, item) => sum + item.length, 0);
  const png = new Uint8Array(size);
  png.set(signature, 0);
  let at = signature.length;
  for (const item of chunks) {
    png.set(item, at);
    at += item.length;
  }
  return png;
}

function chunk(type: string, data: Uint8Array): Uint8Array {
  const body = new Uint8Array(4 + data.length);
  for (let index = 0; index < 4; index += 1) body[index] = type.charCodeAt(index);
  body.set(data, 4);
  const out = new Uint8Array(12 + data.length);
  writeUint32(out, 0, data.length);
  out.set(body, 4);
  writeUint32(out, 8 + data.length, crc32(body));
  return out;
}

function writeUint32(target: Uint8Array, offset: number, value: number): void {
  const next = value >>> 0;
  target[offset] = (next >>> 24) & 0xff;
  target[offset + 1] = (next >>> 16) & 0xff;
  target[offset + 2] = (next >>> 8) & 0xff;
  target[offset + 3] = next & 0xff;
}

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let index = 0; index < data.length; index += 1) {
    crc ^= data[index] ?? 0;
    for (let bit = 0; bit < 8; bit += 1) {
      const mask = -(crc & 1);
      crc = (crc >>> 1) ^ (0xedb88320 & mask);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}
